// ---- Heraldic furniture ----
// Castle/great-hall-specific pieces: a throne, a banquet table, a grand canopied
// four-poster bed, hanging tapestries, and a wall crest. These carry the royal
// palette (gold, velvet, ermine, heraldic cloths). Theme-neutral pieces (bed,
// table, chest, shelf, hearth, rug, …) live in common.ts. Same conventions: built
// from meshkit prims via the ctx, authored feet-at-y=0 in the building's LOCAL
// coords; SOLID pieces keep() + collide; wall hangings shell(nx,nz) so they fade
// with their host wall.

import { shade } from "../../color.ts";
import {
  type Ctx, WOOD, WOOD_LT, WOOD_DK, GOLD, CUSHION, STONE_DAIS,
  CANDLE, FLAME, LINEN, ERMINE_PILLOW, CLOTH_RED, WALL_MOUNT,
} from "./shared.ts";

// ---- Throne ------------------------------------------------------------------
// A raised stone dais carrying a tall oak throne with red cushions, gold arm
// finials, a crowning crest and two spire finials. Faces +z (toward the gate).
export function throne(c: Ctx, cx: number, cz: number) {
  const { box, cone, cylinder, keep, collideBox } = c;
  keep(() => {
    box(cx, 0.1, cz, 5.0, 0.2, 4.2, STONE_DAIS);
    box(cx, 0.3, cz, 3.8, 0.2, 3.2, shade(STONE_DAIS, 0.05));
    box(cx, 0.85, cz, 2.2, 0.5, 1.8, WOOD);            // seat block (top ~1.1)
    box(cx, 2.0, cz - 0.78, 2.2, 2.8, 0.35, WOOD);     // backrest (0.6..3.4)
    box(cx, 1.7, cz - 0.6, 1.5, 1.7, 0.12, CUSHION);   // back cushion
    box(cx, 1.16, cz + 0.05, 1.7, 0.16, 1.45, CUSHION); // seat cushion
    for (const sx of [-1, 1]) {
      box(cx + sx * 1.05, 1.3, cz, 0.3, 0.5, 1.7, WOOD_LT);
      cylinder(cx + sx * 1.05, 1.55, cz + 0.78, 0.14, 0.14, 0.22, GOLD);
      cone(cx + sx * 0.92, 3.4, cz - 0.78, 0.18, 0.6, GOLD); // back spire finial
    }
    box(cx, 3.45, cz - 0.74, 0.9, 0.55, 0.14, GOLD);
    box(cx, 3.45, cz - 0.68, 0.34, 0.34, 0.1, CUSHION);
  });
  (c.standBox ?? collideBox)(cx - 1.3, cx + 1.3, cz - 1.15, cz + 1.05, 0, 1.2); // top standable (gravity plan)
  // One royal seat on the cushion (1.16 + 0.08), facing out over the dais (+z).
  c.seat?.(cx, 1.24, cz + 0.05, 0);
}

// ---- Banquet table -----------------------------------------------------------
// A long oak table running along z, set with pewter plates, gold goblets and a
// branched candelabra centrepiece.
export function banquetTable(c: Ctx, cx: number, cz: number, length: number) {
  const { box, cylinder, cone, keep, collideBox } = c;
  const w = 1.6, topY = 1.05;
  keep(() => {
    box(cx, topY, cz, w, 0.16, length, WOOD_LT);            // table top
    box(cx, topY - 0.12, cz, w - 0.3, 0.1, length - 0.2, WOOD_DK); // apron
    const lx = w / 2 - 0.22, lz = length / 2 - 0.3;
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      box(cx + sx * lx, 0.47, cz + sz * lz, 0.24, 0.94, 0.24, WOOD); // legs
    }
    const settings = Math.max(2, Math.floor(length / 2.0));
    for (let i = 0; i < settings; i++) {
      const z = cz - length / 2 + (i + 0.5) * (length / settings);
      const sx = i % 2 === 0 ? -1 : 1;
      cylinder(cx + sx * 0.42, 1.13, z, 0.26, 0.26, 0.04, 0xb9bcc4); // plate
      cylinder(cx + sx * 0.42, 1.2, z + 0.36, 0.07, 0.09, 0.22, GOLD); // goblet
    }
    cylinder(cx, 1.13, cz, 0.16, 0.22, 0.18, GOLD); // base
    cylinder(cx, 1.3, cz, 0.06, 0.06, 0.5, GOLD);   // stem
    for (const sx of [-1, 0, 1]) {
      const bx = cx + sx * 0.32;
      cylinder(bx, 1.78, cz, 0.05, 0.05, 0.28, CANDLE);
      cone(bx, 2.06, cz, 0.06, 0.16, FLAME);
    }
  });
  (c.standBox ?? collideBox)(cx - w / 2, cx + w / 2, cz - length / 2, cz + length / 2, 0, 1.05);
}

// ---- Four-poster bed ---------------------------------------------------------
// A grand canopied bed: oak frame + carved headboard, pale linen mattress with a
// deep-red blanket folded over the foot, two pillows at the head (toward -z), four
// turned posts rising to a draped canopy. `y0` lifts it onto an upper floor.
export function fourPosterBed(c: Ctx, cx: number, cz: number, y0 = 0) {
  const { box, cylinder, keep, collideBox } = c;
  const W = 2.8, L = 4.6, mattY = 0.95, postH = 3.1;
  keep(() => {
    box(cx, y0 + 0.4, cz, W, 0.5, L, WOOD);                              // frame
    box(cx, y0 + mattY, cz, W - 0.3, 0.42, L - 0.3, LINEN);             // mattress
    box(cx, y0 + mattY + 0.16, cz + L * 0.16, W - 0.3, 0.18, L * 0.52, CUSHION); // blanket over the foot
    for (const sx of [-1, 1])
      box(cx + sx * 0.62, y0 + mattY + 0.24, cz - L / 2 + 0.6, 0.9, 0.3, 0.78, ERMINE_PILLOW); // pillows
    box(cx, y0 + 1.7, cz - L / 2 + 0.12, W, 2.2, 0.22, WOOD_DK);
    box(cx, y0 + 2.55, cz - L / 2 + 0.12, W * 0.5, 0.5, 0.26, GOLD);    // headboard crest
    const px = W / 2 - 0.16, pz = L / 2 - 0.16;
    for (const sx of [-1, 1]) for (const sz of [-1, 1])
      cylinder(cx + sx * px, y0, cz + sz * pz, 0.13, 0.16, postH, WOOD);
    box(cx, y0 + postH, cz, W + 0.1, 0.18, L + 0.1, WOOD_DK);           // canopy rail
    box(cx, y0 + postH + 0.14, cz, W + 0.24, 0.14, L + 0.24, CLOTH_RED); // canopy cloth
  });
  (c.standBox ?? collideBox)(cx - W / 2, cx + W / 2, cz - L / 2, cz + L / 2, y0, y0 + 1.3);
}

// ---- Tapestry ----------------------------------------------------------------
// A hanging cloth banner on a wall, on a wooden rod, with gold trim bands, a
// central emblem and a fringed hem. `axis` is the direction the cloth WIDTH runs
// (x for north/south walls, z for east/west walls); (nx,nz) is the host wall's
// outward normal so it fades with that wall. (x,y,z) is the wall's inner face.
export function tapestry(
  c: Ctx, x: number, y: number, z: number,
  axis: "x" | "z", w: number, h: number,
  nx: 0 | 1 | -1, nz: 0 | 1 | -1,
  cloth: number, emblem: number,
) {
  const { box, shell } = c;
  const t = 0.08;
  const ix = -nx, iz = -nz;
  const bx = x + ix * WALL_MOUNT, bz = z + iz * WALL_MOUNT;
  const slab = (px: number, py: number, pz: number, along: number, hy: number, th: number, col: number) =>
    axis === "x" ? box(px, py, pz, along, hy, th, col) : box(px, py, pz, th, hy, along, col);
  shell(nx, nz, () => {
    slab(bx, y, bz, w, h, t, cloth);                                  // cloth field
    slab(bx, y + h / 2 - 0.16, bz, w, 0.2, t + 0.02, GOLD);          // top trim band
    slab(bx, y - h / 2 + 0.16, bz, w, 0.2, t + 0.02, GOLD);          // bottom trim band
    slab(bx + ix * 0.05, y + h / 2 + 0.12, bz + iz * 0.05, w + 0.5, 0.14, 0.16, WOOD_DK); // rod
    slab(bx + ix * 0.05, y, bz + iz * 0.05, w * 0.45, w * 0.45, t, emblem);  // central emblem
    slab(bx + ix * 0.08, y, bz + iz * 0.08, w * 0.2, w * 0.2, t, cloth);
    const fr = 5;
    for (let i = 0; i < fr; i++) {
      const off = -w / 2 + 0.2 + (i + 0.5) * ((w - 0.4) / fr);
      const px = axis === "x" ? bx + off : bx;
      const pz = axis === "x" ? bz : bz + off;
      slab(px, y - h / 2 - 0.12, pz, (w - 0.4) / fr - 0.06, 0.22, t, GOLD);
    }
  });
}

// ---- Wall crest --------------------------------------------------------------
// A round heraldic shield mounted flat on a wall, facing into the room along the
// wall's outward normal, with a contrasting cross. (x,y,z) is the wall's inner face.
export function wallCrest(
  c: Ctx, x: number, y: number, z: number,
  nx: 0 | 1 | -1, nz: 0 | 1 | -1,
  base: number, accent: number,
) {
  const { tube, box, shell } = c;
  const IRON = 0x35363c;
  const axis: "x" | "z" = nx !== 0 ? "x" : "z";
  const ix = -nx, iz = -nz;
  const bx = x + ix * WALL_MOUNT, bz = z + iz * WALL_MOUNT;
  shell(nx, nz, () => {
    tube(bx, y, bz, 0.78, 0.16, IRON, axis);                       // rim/backing
    tube(bx + ix * 0.1, y, bz + iz * 0.1, 0.62, 0.12, base, axis); // shield face, proud of the rim
    const ox = ix * 0.2, oz = iz * 0.2;
    const wide = axis === "x" ? 0.16 : 0.9;
    const deep = axis === "x" ? 0.9 : 0.16;
    box(bx + ox, y, bz + oz, wide, 0.9, deep, accent);            // vertical bar
    box(bx + ox, y, bz + oz, axis === "x" ? 0.16 : 0.9, 0.18, axis === "x" ? 0.9 : 0.16, accent); // cross bar
  });
}
